import { useState, useEffect, useRef } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  MessageSquare, Activity, Send, Trash2, Clock, User,
  ArrowRight, CheckCircle, AlertCircle, Tag, CalendarIcon, Pencil, CheckCircle2
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: 'to_be_done', label: 'To Be Done' },
  { value: 'doing', label: 'Doing' },
  { value: 'need_clarification', label: 'Need Clarification' },
  { value: 'done', label: 'Done' },
];

const TRANSITION_STATUSES = [
  { value: 'to_be_done', label: 'To Be Done' },
  { value: 'doing', label: 'Doing' },
  { value: 'need_clarification', label: 'Need Clarification' },
];

const statusColors = {
  to_be_done: { bg: '#E2E8F0', text: '#34495e' },
  doing: { bg: '#f39c12', text: '#FFFFFF' },
  need_clarification: { bg: '#e74c3c', text: '#FFFFFF' },
  done: { bg: '#27ae60', text: '#FFFFFF' },
};

export default function TaskDetailSheet({ task, open, onClose, users, categories, onTaskUpdated }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('feed');
  const [editingEst, setEditingEst] = useState(false);
  const [estDate, setEstDate] = useState(undefined);
  const [estTime, setEstTime] = useState('12:00');
  const [showDoneConfirm, setShowDoneConfirm] = useState(false);
  const scrollRef = useRef(null);

  const isOverdue = task && task.status !== 'done' && task.estimated_completion && new Date() > new Date(task.estimated_completion);
  const canModify = user && task && (user.role === 'admin' || user.user_id === task.assigned_to);
  const canEditEstimate = canModify;

  useEffect(() => {
    if (open && task) {
      fetchComments();
      fetchActivities();
      setEditingEst(false);
    }
  }, [open, task?.task_id]);

  const fetchComments = async () => {
    try {
      const res = await api.get(`/tasks/${task.task_id}/comments`);
      setComments(res.data.comments);
    } catch (err) { console.error('Failed to fetch comments:', err); }
  };

  const fetchActivities = async () => {
    try {
      const res = await api.get(`/tasks/${task.task_id}/activity`);
      setActivities(res.data.activities);
    } catch (err) { console.error('Failed to fetch activity:', err); }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setSending(true);
    try {
      await api.post(`/tasks/${task.task_id}/comments`, { text: newComment.trim() });
      setNewComment('');
      fetchComments();
      fetchActivities();
      toast.success('Comment added');
    } catch (err) { toast.error('Failed to add comment'); }
    finally { setSending(false); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/comments/${commentId}`);
      fetchComments();
      toast.success('Comment deleted');
    } catch (err) { toast.error('Failed to delete comment'); }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/tasks/${task.task_id}`, { status: newStatus });
      toast.success('Status updated');
      fetchActivities();
      if (onTaskUpdated) onTaskUpdated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
  };

  const handleMarkDone = () => {
    setShowDoneConfirm(true);
  };

  const confirmMarkDone = async () => {
    try {
      await api.put(`/tasks/${task.task_id}`, { status: 'done' });
      toast.success('Task marked as Done');
      fetchActivities();
      if (onTaskUpdated) onTaskUpdated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
    finally { setShowDoneConfirm(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleAddComment();
    }
  };

  const openEstimateEdit = () => {
    if (task.estimated_completion) {
      const d = new Date(task.estimated_completion);
      setEstDate(d);
      setEstTime(format(d, 'HH:mm'));
    } else {
      setEstDate(undefined);
      setEstTime('12:00');
    }
    setEditingEst(true);
  };

  const saveEstimate = async () => {
    let estimatedCompletion = '';
    if (estDate) {
      const [hours, minutes] = estTime.split(':').map(Number);
      const d = new Date(estDate);
      d.setHours(hours || 0, minutes || 0, 0, 0);
      estimatedCompletion = d.toISOString();
    }
    try {
      await api.put(`/tasks/${task.task_id}`, { estimated_completion: estimatedCompletion || null });
      toast.success('Estimated completion updated');
      setEditingEst(false);
      if (onTaskUpdated) onTaskUpdated();
    } catch (err) { toast.error('Failed to update'); }
  };

  if (!task) return null;

  const sc = statusColors[task.status] || statusColors.to_be_done;
  const getUserName = (userId) => users?.find(u => u.user_id === userId)?.name || 'Unknown';
  const getTaskAssigneeName = () => {
    if (task?.assigned_to_removed_name) return task.assigned_to_removed_name;
    if (!task?.assigned_to) return 'Unassigned';
    return getUserName(task.assigned_to);
  };
  const getCategoryName = (catId) => categories?.find(c => c.category_id === catId)?.name || '-';

  // Merge comments and activities into a unified feed, sorted chronologically
  const feedItems = [];

  comments.forEach(c => {
    feedItems.push({ type: 'comment', ...c, sort_time: c.created_at });
  });

  activities.filter(a => a.action_type !== 'commented').forEach(a => {
    feedItems.push({ type: 'activity', ...a, sort_time: a.created_at });
  });

  feedItems.sort((a, b) => new Date(a.sort_time) - new Date(b.sort_time));

  const renderActivityIcon = (actionType) => {
    switch (actionType) {
      case 'created': return <CheckCircle size={14} className="text-[#27ae60]" />;
      case 'status_changed': return <ArrowRight size={14} className="text-[#f39c12]" />;
      case 'assigned': return <User size={14} className="text-[#34495e]" />;
      default: return <Activity size={14} className="text-[#7F8C8D]" />;
    }
  };

  const renderActivityText = (item) => {
    switch (item.action_type) {
      case 'created':
        return <span><strong>{item.user_name}</strong> created this task</span>;
      case 'status_changed': {
        const oldLabel = STATUS_OPTIONS.find(s => s.value === item.details?.old_status)?.label || item.details?.old_status;
        const newLabel = STATUS_OPTIONS.find(s => s.value === item.details?.new_status)?.label || item.details?.new_status;
        return <span><strong>{item.user_name}</strong> changed status from <em>{oldLabel}</em> to <em>{newLabel}</em></span>;
      }
      case 'assigned':
        return <span><strong>{item.user_name}</strong> assigned to <strong>{item.details?.assigned_to_name || 'someone'}</strong></span>;
      default:
        return <span><strong>{item.user_name}</strong> {item.action_type}</span>;
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg p-0 flex flex-col bg-white border-l border-[#E2E8F0]" data-testid="task-detail-sheet">
        <SheetHeader className="p-6 pb-4 border-b border-[#E2E8F0]">
          <SheetTitle className={`text-lg font-black tracking-tight pr-6 ${isOverdue ? 'text-[#e74c3c]' : 'text-[#34495e]'}`}>
            {isOverdue && <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#e74c3c] mr-2 -mt-0.5" />}
            {task.title}
          </SheetTitle>
          <SheetDescription className="sr-only">Task details and activity feed</SheetDescription>
        </SheetHeader>

        {/* Task Meta */}
        <div className="px-6 py-4 border-b border-[#E2E8F0] space-y-3 bg-[#F8F9FA]">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Status</span>
            <div className="flex items-center gap-2">
              {canModify && task.status !== 'done' ? (
                <Select value={task.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="h-auto w-auto border-0 p-0 shadow-none" data-testid="sheet-status-select">
                    <span className="inline-block px-2.5 py-1 rounded-sm text-xs font-bold" style={{ backgroundColor: sc.bg, color: sc.text }}>
                      {STATUS_OPTIONS.find(s => s.value === task.status)?.label}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSITION_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <span className="inline-block px-2.5 py-1 rounded-sm text-xs font-bold" style={{ backgroundColor: sc.bg, color: sc.text }}>
                  {STATUS_OPTIONS.find(s => s.value === task.status)?.label}
                </span>
              )}
              {canModify && task.status !== 'done' && (
                <button
                  onClick={handleMarkDone}
                  className="flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-bold text-[#27ae60] border border-[#27ae60] hover:bg-[#27ae60] hover:text-white transition-colors"
                  data-testid="sheet-mark-done-btn"
                >
                  <CheckCircle2 size={14} /> Done
                </button>
              )}
            </div>
          </div>
          {task.description && (
            <p className="text-sm text-[#34495e] leading-relaxed">{task.description}</p>
          )}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1.5 text-[#7F8C8D]">
              <Tag size={12} />
              <span>{getCategoryName(task.category_id)}</span>
            </div>
            <div className="flex items-center gap-1.5 text-[#7F8C8D]">
              <User size={12} />
              <span className={task.assigned_to_removed_name ? 'text-[#e74c3c] italic' : ''}>{getTaskAssigneeName()}</span>
            </div>
            <div className={`flex items-center gap-1.5 ${isOverdue ? 'text-[#e74c3c] font-bold' : 'text-[#7F8C8D]'}`}>
              <Clock size={12} />
              {editingEst ? (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-6 text-[10px] rounded-sm border-[#E2E8F0] px-2" data-testid="sheet-est-date-picker">
                        <CalendarIcon size={10} className="mr-1" />
                        {estDate ? format(estDate, 'MMM d') : 'Date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={estDate} onSelect={setEstDate} fromDate={new Date()} toDate={addDays(new Date(), 14)} />
                    </PopoverContent>
                  </Popover>
                  <Input type="time" value={estTime} onChange={(e) => setEstTime(e.target.value)} className="h-6 w-20 text-[10px] rounded-sm border-[#E2E8F0] px-1" data-testid="sheet-est-time-input" />
                  <Button size="sm" onClick={saveEstimate} className="h-6 px-2 text-[10px] bg-[#27ae60] hover:bg-[#219a52] text-white rounded-sm" data-testid="sheet-est-save-btn">Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingEst(false)} className="h-6 px-2 text-[10px] text-[#7F8C8D] rounded-sm" data-testid="sheet-est-cancel-btn">Cancel</Button>
                </div>
              ) : (
                <span className="flex items-center gap-1">
                  {task.estimated_completion ? format(new Date(task.estimated_completion), 'MMM d, yyyy HH:mm') : 'No estimate'}
                  {isOverdue && <span className="text-[10px] ml-1">(Overdue)</span>}
                  {canEditEstimate && (
                    <button onClick={openEstimateEdit} className="ml-1 p-0.5 text-[#7F8C8D] hover:text-[#34495e] transition-colors" data-testid="sheet-edit-est-btn">
                      <Pencil size={10} />
                    </button>
                  )}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-[#7F8C8D]">
              <Activity size={12} />
              <span>Created {format(new Date(task.created_at), 'MMM d, yyyy')}</span>
            </div>
            {task.slack_channel_id && (
              <div className="flex items-center gap-1.5 text-[#7F8C8D] col-span-2" data-testid="task-slack-channel-display">
                <Activity size={12} />
                <span>Slack channel on completion: {task.slack_channel_id}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-[#E2E8F0] px-6">
          <button
            onClick={() => setActiveTab('feed')}
            className={`px-3 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
              activeTab === 'feed' ? 'border-[#34495e] text-[#34495e]' : 'border-transparent text-[#BDC3C7] hover:text-[#7F8C8D]'
            }`}
            data-testid="tab-feed"
          >
            <MessageSquare size={12} className="inline mr-1.5 -mt-0.5" />
            Feed ({feedItems.length})
          </button>
          <button
            onClick={() => setActiveTab('comments')}
            className={`px-3 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
              activeTab === 'comments' ? 'border-[#34495e] text-[#34495e]' : 'border-transparent text-[#BDC3C7] hover:text-[#7F8C8D]'
            }`}
            data-testid="tab-comments"
          >
            <MessageSquare size={12} className="inline mr-1.5 -mt-0.5" />
            Comments ({comments.length})
          </button>
        </div>

        {/* Feed / Comments */}
        <ScrollArea className="flex-1 px-6" ref={scrollRef}>
          <div className="py-4 space-y-3">
            {activeTab === 'feed' ? (
              feedItems.length === 0 ? (
                <p className="text-center text-sm text-[#BDC3C7] py-8">No activity yet</p>
              ) : (
                feedItems.map((item, i) => (
                  item.type === 'comment' ? (
                    <div key={`c-${item.comment_id}`} className="bg-white border border-[#E2E8F0] rounded-sm p-3" data-testid={`comment-${item.comment_id}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-sm bg-[#34495e] flex items-center justify-center text-white text-[10px] font-bold">
                            {item.user_name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <span className="text-xs font-bold text-[#34495e]">{item.user_name}</span>
                          <span className="text-[10px] text-[#BDC3C7]">{format(new Date(item.created_at), 'MMM d, HH:mm')}</span>
                        </div>
                        {(item.user_id === user?.user_id || user?.role === 'admin') && (
                          <button
                            onClick={() => handleDeleteComment(item.comment_id)}
                            className="p-1 text-[#BDC3C7] hover:text-[#e74c3c] transition-colors"
                            data-testid={`delete-comment-${item.comment_id}`}
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-[#34495e] leading-relaxed whitespace-pre-wrap">{item.text}</p>
                    </div>
                  ) : (
                    <div key={`a-${item.activity_id}`} className="flex items-start gap-2.5 py-1" data-testid={`activity-${item.activity_id}`}>
                      <div className="mt-0.5">{renderActivityIcon(item.action_type)}</div>
                      <div className="flex-1">
                        <p className="text-xs text-[#7F8C8D] leading-relaxed">{renderActivityText(item)}</p>
                        <p className="text-[10px] text-[#BDC3C7]">{format(new Date(item.created_at), 'MMM d, HH:mm')}</p>
                      </div>
                    </div>
                  )
                ))
              )
            ) : (
              comments.length === 0 ? (
                <p className="text-center text-sm text-[#BDC3C7] py-8">No comments yet. Be the first!</p>
              ) : (
                comments.map(c => (
                  <div key={c.comment_id} className="bg-white border border-[#E2E8F0] rounded-sm p-3" data-testid={`comment-only-${c.comment_id}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-sm bg-[#34495e] flex items-center justify-center text-white text-[10px] font-bold">
                          {c.user_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <span className="text-xs font-bold text-[#34495e]">{c.user_name}</span>
                        <span className="text-[10px] text-[#BDC3C7]">{format(new Date(c.created_at), 'MMM d, HH:mm')}</span>
                      </div>
                      {(c.user_id === user?.user_id || user?.role === 'admin') && (
                        <button
                          onClick={() => handleDeleteComment(c.comment_id)}
                          className="p-1 text-[#BDC3C7] hover:text-[#e74c3c] transition-colors"
                          data-testid={`delete-cmt-${c.comment_id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-[#34495e] leading-relaxed whitespace-pre-wrap">{c.text}</p>
                  </div>
                ))
              )
            )}
          </div>
        </ScrollArea>

        {/* Comment Input */}
        <div className="p-4 border-t border-[#E2E8F0] bg-white">
          <div className="flex gap-2">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Write a comment... (Ctrl+Enter to send)"
              className="flex-1 rounded-sm border-[#E2E8F0] text-sm resize-none min-h-[60px]"
              data-testid="comment-input"
            />
            <Button
              onClick={handleAddComment}
              disabled={sending || !newComment.trim()}
              className="bg-[#34495e] hover:bg-[#2c3e50] text-white rounded-sm self-end h-10 w-10 p-0"
              data-testid="send-comment-btn"
            >
              <Send size={16} />
            </Button>
          </div>
        </div>
      </SheetContent>

      {/* Confirm Mark as Done */}
      <AlertDialog open={showDoneConfirm} onOpenChange={(open) => { if (!open) setShowDoneConfirm(false); }}>
        <AlertDialogContent className="bg-white border border-[#E2E8F0] rounded-sm" data-testid="confirm-done-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-black text-[#34495e]">Mark task as Done?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#7F8C8D]">
              Are you sure you want to mark this task as Done? This will trigger any configured notifications.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm border-[#E2E8F0] text-[#7F8C8D]" data-testid="confirm-done-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmMarkDone} className="rounded-sm bg-[#27ae60] hover:bg-[#219a52] text-white" data-testid="confirm-done-yes">Yes, mark as Done</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
